<template>
  <div class="freeform-select" :data-testid="rootTestId">
    <v-menu
      v-model="isMenuOpen"
      offset-y
      :close-on-content-click="false"
      :open-on-click="false"
      :disabled="disabled"
      :nudge-width="0"
      :z-index="menuZIndex"
      max-height="20rem"
      content-class="freeform-select__menu"
      :data-testid="`${rootTestId}__menu`"
    >
      <!-- Bind on/attrs on a native wrapper so Vuetify can place the menu.
           Do not bind them on the text field — that turns the input into a click dropdown. -->
      <template v-slot:activator="{ on, attrs }">
        <div
          class="freeform-select__activator"
          v-bind="attrs"
          v-on="on"
        >
          <v-text-field
            outlined
            dense
            :value="value"
            :placeholder="placeholder"
            :error-messages="error"
            :hide-details="hideDetails"
            :disabled="disabled"
            :clearable="false"
            append-icon="mdi-chevron-down"
            class="freeform-select__field"
            autocomplete="off"
            :data-testid="`${rootTestId}__input`"
            @input="onType"
            @click="openMenu"
            @click:append.stop="toggleMenu"
            @keydown.esc="closeMenu"
          />
        </div>
      </template>

      <v-list
        dense
        class="freeform-select__list"
        :data-testid="`${rootTestId}__list`"
      >
        <v-list-item
          v-for="(item, index) in visibleItems"
          :key="`${item}--${index}`"
          class="freeform-select__item"
          :class="{ 'freeform-select__item--active': isActiveItem(item) }"
          :data-testid="`${rootTestId}__item--${index}`"
          @mousedown.prevent
          @click="selectItem(item)"
        >
          <v-list-item-title class="freeform-select__item-title">
            {{ item }}
          </v-list-item-title>
        </v-list-item>
        <v-list-item
          v-if="!visibleItems.length"
          disabled
          class="freeform-select__item freeform-select__item--empty"
          :data-testid="`${rootTestId}__empty`"
        >
          <v-list-item-title class="freeform-select__item-title">
            No matches
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
  </div>
</template>

<script>
import { DIALOG_MENU_Z_INDEX } from '@/utils/sync-attached-menu';
import './FreeformSelect.scss';

/**
 * Outlined text field + menu of string options.
 * Menu click always emits that item (does not depend on combobox filtering).
 * Typing a string not in the list is a valid value.
 */
export default {
  name: 'FreeformSelect',

  inheritAttrs: false,

  props: {
    value: {
      type: String,
      default: '',
    },
    items: {
      type: Array,
      default: () => [],
    },
    placeholder: {
      type: String,
      default: 'Select',
    },
    error: {
      type: [String, Array],
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dataTestid: {
      type: String,
      default: 'freeform-select',
    },
  },

  data() {
    return {
      isMenuOpen: false,
      valueAtOpen: '',
      typedQuery: '',
      menuZIndex: DIALOG_MENU_Z_INDEX,
    };
  },

  computed: {
    rootTestId() {
      return this.$attrs['data-testid'] || this.dataTestid;
    },

    hideDetails() {
      if (Array.isArray(this.error)) return this.error.length === 0;
      return !this.error;
    },

    itemStrings() {
      return (this.items || [])
        .map((item) => String(item == null ? '' : item).trim())
        .filter(Boolean);
    },

    visibleItems() {
      const query = String(this.typedQuery || '').trim().toLowerCase();
      if (!query) return this.itemStrings;
      return this.itemStrings.filter((item) => item.toLowerCase().includes(query));
    },
  },

  watch: {
    isMenuOpen(open) {
      if (!open) {
        this.typedQuery = '';
      }
    },
    value(next) {
      if (!this.isMenuOpen) return;
      if (String(next || '') === String(this.valueAtOpen || '')) {
        this.typedQuery = '';
      }
    },
  },

  beforeDestroy() {
    this.isMenuOpen = false;
    this.typedQuery = '';
    this.valueAtOpen = '';
  },

  methods: {
    closeMenu() {
      this.isMenuOpen = false;
    },

    openMenu() {
      if (this.disabled || this.isMenuOpen) return;
      this.valueAtOpen = String(this.value || '');
      this.isMenuOpen = true;
    },

    toggleMenu() {
      if (this.disabled) return;
      if (this.isMenuOpen) {
        this.closeMenu();
        return;
      }
      this.openMenu();
    },

    onType(text) {
      const next = text == null ? '' : String(text);
      if (!this.isMenuOpen) {
        this.valueAtOpen = String(this.value || '');
      }
      this.openMenu();
      this.$emit('input', next);
      if (!this.isMenuOpen) return;
      const openedValue = String(this.valueAtOpen || '');
      this.typedQuery = next === openedValue ? '' : next;
    },

    selectItem(item) {
      const next = String(item || '');
      this.$emit('input', next);
      this.valueAtOpen = next;
      this.typedQuery = '';
      this.isMenuOpen = false;
    },

    isActiveItem(item) {
      return String(item || '') === String(this.value || '');
    },
  },
};
</script>
